import { useState, useCallback } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { toast } from "sonner";

export interface ApiToken {
  _id: string;
  _creationTime: number;
  name: string;
  lastUsedAt?: number;
  revokedAt?: number;
}

// Type assertion for the apiTokens module until Convex generates it
const apiTokens = (api as any).apiTokens || {
  listTokens: "apiTokens:listTokens",
  createToken: "apiTokens:createToken",
  revokeToken: "apiTokens:revokeToken",
};

export function useApiTokens() {
  const listTokens = useQuery(apiTokens.listTokens as any);
  const createToken = useMutation(apiTokens.createToken as any);
  const revokeToken = useMutation(apiTokens.revokeToken as any);

  const [isCreating, setIsCreating] = useState(false);
  const [isRevoking, setIsRevoking] = useState<string | null>(null);
  const [newToken, setNewToken] = useState<{ token: string; tokenId: string } | null>(null);

  const handleCreateToken = useCallback(async (name: string) => {
    setIsCreating(true);
    try {
      const result = await createToken({ name });
      setNewToken({ token: result.token, tokenId: result.tokenId });
      toast.success("API token created successfully");
      return result;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create token");
      throw error;
    } finally {
      setIsCreating(false);
    }
  }, [createToken]);

  const handleRevokeToken = useCallback(async (tokenId: string) => {
    setIsRevoking(tokenId);
    try {
      await revokeToken({ tokenId });
      toast.success("API token revoked successfully");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to revoke token");
      throw error;
    } finally {
      setIsRevoking(null);
    }
  }, [revokeToken]);

  const closeNewTokenModal = useCallback(() => {
    setNewToken(null);
  }, []);

  return {
    tokens: listTokens ?? [],
    isLoading: listTokens === undefined,
    isCreating,
    isRevoking: isRevoking !== null,
    isRevokingToken: isRevoking,
    createToken: handleCreateToken,
    revokeToken: handleRevokeToken,
    newToken,
    closeNewTokenModal,
  };
}
